import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { Button } from 'reactstrap';
import { useNavigate, useParams } from 'react-router';
import { useCookies } from 'react-cookie';
import Sidebar from '../../Sidebar';
import FormPageLayout from '../../components/FormPageLayout';
import LoadingOverlay from '../../components/LoadingOverlay';
import FloatingControls from '../../components/FloatingControls';
import { displayError } from '../../helpers';
import {
    cancelContraVoucher,
    deleteContraVoucher,
    getContraVoucherDetails,
} from '../../services/ContraVoucherServices';
import { AccountingHintPanel } from './VoucherUtils';

function ContraVoucherEdit() {
    const { id } = useParams();
    const [cookies] = useCookies(['myToken']);
    const navigate = useNavigate();
    const [record, setRecord] = useState(null);
    const [inProgress, setInProgress] = useState(true);

    useEffect(() => {
        if (!id) {
            return;
        }
        setInProgress(true);
        getContraVoucherDetails(cookies, id)
            .then((response) => setRecord(response))
            .catch((error) => displayError(error?.response?.data || error, 'Load Failed'))
            .finally(() => setInProgress(false));
    }, [cookies, id]);

    const handleCancel = () => {
        Swal.fire({
            title: 'Cancel contra voucher?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, cancel',
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }
            setInProgress(true);
            cancelContraVoucher(cookies, id)
                .then((response) => {
                    Swal.fire('Cancelled', response.message || 'Contra voucher cancelled.', 'success');
                    return getContraVoucherDetails(cookies, id);
                })
                .then((response) => setRecord(response))
                .catch((error) => displayError(error?.response?.data || error, 'Cancel Failed'))
                .finally(() => setInProgress(false));
        });
    };

    const handleDelete = () => {
        Swal.fire({
            title: 'Delete cancelled contra voucher?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete',
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }
            setInProgress(true);
            deleteContraVoucher(cookies, id)
                .then((response) => {
                    Swal.fire('Deleted', response.message || 'Contra voucher deleted.', 'success');
                    navigate('/ContraVoucherTable');
                })
                .catch((error) => displayError(error?.response?.data || error, 'Delete Failed'))
                .finally(() => setInProgress(false));
        });
    };

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <FormPageLayout
                    moduleLabel="ACCOUNTS"
                    title="Contra Voucher"
                    subtitle={record?.contra_no || ''}
                    modeLabel="VIEW MODE"
                >
                    {record && (
                        <>
                            <AccountingHintPanel hint={record.accounting_hint} />
                            <div className="form-row table-bordered shadow p-3 my-2 border-secondary form-control-panel">
                                <div className="row mb-2">
                                    <div className="col-sm-3"><strong>Plant</strong><div>{record.plant_alias}</div></div>
                                    <div className="col-sm-3"><strong>Contra No</strong><div>{record.contra_no}</div></div>
                                    <div className="col-sm-3"><strong>Contra Date</strong><div>{record.contra_date}</div></div>
                                    <div className="col-sm-3"><strong>Status</strong><div>{record.status ? 'Active' : 'Cancelled'}</div></div>
                                </div>
                                <div className="row mb-2">
                                    <div className="col-sm-4"><strong>From Bank (Cr)</strong><div>{record.from_bank_name}</div></div>
                                    <div className="col-sm-4"><strong>To Bank (Dr)</strong><div>{record.to_bank_name}</div></div>
                                    <div className="col-sm-4"><strong>Amount</strong><div>{record.contra_amount}</div></div>
                                </div>
                                <div className="row mb-2">
                                    <div className="col-sm-3"><strong>Mode</strong><div>{record.transfer_mode}</div></div>
                                    <div className="col-sm-3"><strong>Bank Date</strong><div>{record.bank_date}</div></div>
                                    <div className="col-sm-3"><strong>Instrument</strong><div>{record.instrument_no || '—'}</div></div>
                                </div>
                                {record.remarks && (
                                    <div className="row"><div className="col-sm-12"><strong>Remarks</strong><div>{record.remarks}</div></div></div>
                                )}
                            </div>
                        </>
                    )}
                    <FloatingControls>
                        {record?.status ? (
                            <Button color="warning" type="button" onClick={handleCancel}>Cancel Voucher</Button>
                        ) : (
                            <Button color="danger" type="button" onClick={handleDelete}>Delete</Button>
                        )}
                        <Button color="secondary" type="button" onClick={() => navigate('/ContraVoucherTable')}>Back to List</Button>
                    </FloatingControls>
                </FormPageLayout>
                <LoadingOverlay active={inProgress} />
            </div>
        </div>
    );
}

export default ContraVoucherEdit;
